// src/contexts/CartContext.tsx

import React, { createContext, useState, ReactNode } from "react";
import { useLocalStorage } from "./useLocalStorage";
import {
  OrderItem,
  getVariationSku,
  getVariationPriceCents,
} from "../types/OrderItem";
import { trackAddToCart } from "../lib/meta-pixel";

interface CartContextType {
  cart: OrderItem[];
  isCartOpen: boolean;
  setIsCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addToCart: (item: OrderItem, quantity: number) => void;
  removeFromCart: (item: OrderItem, quantity: number) => void;
  removeAllFromCart: (item: OrderItem) => void;
  resetCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [cart, setCart] = useLocalStorage<OrderItem[]>(
    "americana-company-cart",
    [],
  );
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);

  const addToCart = (item: OrderItem, quantity: number) => {
    // Meta pixel AddToCart. Reported here rather than at the button so every
    // path (product page, cross-sell, wishlist, Buy Again, ?products= deep
    // link) is covered. No-ops until the pixel is initialized, so this stays
    // silent for visitors who declined tracking or send GPC.
    if (item.variation) {
      trackAddToCart({
        sku: getVariationSku(item.variation),
        name: item.product?.itemData?.name ?? "",
        priceCents: getVariationPriceCents(item.variation),
        quantity,
      });
    }
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (i) =>
          i.product.id === item.product.id &&
          i.variation.id === item.variation.id,
      );
      if (existingItem) {
        return prevCart.map((i) =>
          i.product.id === item.product.id &&
          i.variation.id === item.variation.id
            ? { ...i, quantity: i.quantity + quantity }
            : i,
        );
      }
      return [...prevCart, { ...item, quantity: quantity }];
    });
  };

  const removeFromCart = (item: OrderItem, quantity: number) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (i) =>
          i.product.id === item.product.id &&
          i.variation.id === item.variation.id,
      );
      if (
        existingItem &&
        existingItem.quantity > 1 &&
        existingItem.quantity - quantity >= 0
      ) {
        return prevCart.map((i) =>
          i.product.id === item.product.id &&
          i.variation.id === item.variation.id
            ? { ...i, quantity: i.quantity - quantity }
            : i,
        );
      }
      return prevCart.filter(
        (i) =>
          i.product.id !== item.product.id ||
          i.variation.id !== item.variation.id,
      );
    });
  };

  const removeAllFromCart = (item: OrderItem) => {
    setCart((prevCart) =>
      prevCart.filter(
        (i) =>
          i.product.id !== item.product.id ||
          i.variation.id !== item.variation.id,
      ),
    );
  };

  const resetCart = () => {
    setCart([]);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        resetCart,
        isCartOpen,
        setIsCartOpen,
        addToCart,
        removeFromCart,
        removeAllFromCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = React.useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
